import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProductsPage } from './pages/ProductsPage';
import { ContactPage } from './pages/ContactPage';
import { ManagementPage } from './pages/ManagementPage';
import { AdvantagesPage } from './pages/AdvantagesPage';
import { ConsultationPage } from './pages/ConsultationPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { LanguageProvider, useLanguage, getCanonicalSlug } from './contexts/LanguageContext';
// Scroll to top component
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
// Language route wrapper component
function LanguageRouteWrapper({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    lang
  } = useParams<{
    lang: string;
  }>();
  const {
    setLanguage
  } = useLanguage();
  useEffect(() => {
    if (lang === 'id' || lang === 'en') {
      setLanguage(lang);
    }
  }, [lang, setLanguage]);
  return <>{children}</>;
}
// Page route wrapper that handles slug matching
function PageRoute({
  page,
  slugs
}: {
  page: React.ReactNode;
  slugs: string[];
}) {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const canonicalSlug = slug ? getCanonicalSlug(slug) : '';
  // Check if current slug matches any of the valid slugs for this page
  const isValidSlug = slugs.some(s => getCanonicalSlug(s) === canonicalSlug);
  if (!isValidSlug && slug) {
    return <Navigate to="/id" replace data-id="element-0" />;
  }
  return <LanguageRouteWrapper data-id="element-1">{page}</LanguageRouteWrapper>;
}
// All routes component
function AppRoutes() {
  return <Routes data-id="element-2">
      {/* Root redirects to /id by default */}
      <Route path="/" element={<Navigate to="/id" replace data-id="element-4" />} data-id="element-3" />

      {/* Language-prefixed home routes */}
      <Route path="/:lang" element={<LanguageRouteWrapper data-id="element-6">
            <HomePage data-id="element-7" />
          </LanguageRouteWrapper>} data-id="element-5" />

      {/* About Us - tentang-kami (ID) / about-us (EN) */}
      <Route path="/:lang/tentang-kami" element={<PageRoute page={<AboutPage data-id="element-10" />} slugs={['tentang-kami']} data-id="element-9" />} data-id="element-8" />
      <Route path="/:lang/about-us" element={<PageRoute page={<AboutPage data-id="element-13" />} slugs={['about-us']} data-id="element-12" />} data-id="element-11" />

      {/* Management - manajemen (ID) / management (EN) */}
      <Route path="/:lang/manajemen" element={<PageRoute page={<ManagementPage data-id="element-16" />} slugs={['manajemen']} data-id="element-15" />} data-id="element-14" />
      <Route path="/:lang/management" element={<PageRoute page={<ManagementPage data-id="element-19" />} slugs={['management']} data-id="element-18" />} data-id="element-17" />

      {/* Projects - proyek (ID) / projects (EN) */}
      <Route path="/:lang/proyek" element={<PageRoute page={<ProjectsPage data-id="element-22" />} slugs={['proyek']} data-id="element-21" />} data-id="element-20" />
      <Route path="/:lang/projects" element={<PageRoute page={<ProjectsPage data-id="element-25" />} slugs={['projects']} data-id="element-24" />} data-id="element-23" />

      {/* Products & Services - produk-layanan (ID) / products-services (EN) */}
      <Route path="/:lang/produk-layanan" element={<PageRoute page={<ProductsPage data-id="element-28" />} slugs={['produk-layanan']} data-id="element-27" />} data-id="element-26" />
      <Route path="/:lang/products-services" element={<PageRoute page={<ProductsPage data-id="element-31" />} slugs={['products-services']} data-id="element-30" />} data-id="element-29" />

      {/* Advantages - keunggulan (ID) / advantages (EN) */}
      <Route path="/:lang/keunggulan" element={<PageRoute page={<AdvantagesPage data-id="element-34" />} slugs={['keunggulan']} data-id="element-33" />} data-id="element-32" />
      <Route path="/:lang/advantages" element={<PageRoute page={<AdvantagesPage data-id="element-37" />} slugs={['advantages']} data-id="element-36" />} data-id="element-35" />

      {/* Consultation - konsultasi (ID) / consultation (EN) */}
      <Route path="/:lang/konsultasi" element={<PageRoute page={<ConsultationPage data-id="element-40" />} slugs={['konsultasi']} data-id="element-39" />} data-id="element-38" />
      <Route path="/:lang/consultation" element={<PageRoute page={<ConsultationPage data-id="element-43" />} slugs={['consultation']} data-id="element-42" />} data-id="element-41" />

      {/* Contact - kontak (ID) / contact (EN) */}
      <Route path="/:lang/kontak" element={<PageRoute page={<ContactPage data-id="element-46" />} slugs={['kontak']} data-id="element-45" />} data-id="element-44" />
      <Route path="/:lang/contact" element={<PageRoute page={<ContactPage data-id="element-49" />} slugs={['contact']} data-id="element-48" />} data-id="element-47" />

      {/* Privacy Policy - kebijakan-privasi (ID) / privacy-policy (EN) */}
      <Route path="/:lang/kebijakan-privasi" element={<LanguageRouteWrapper data-id="element-51">
            <PrivacyPolicyPage data-id="element-52" />
          </LanguageRouteWrapper>} data-id="element-50" />
      <Route path="/:lang/privacy-policy" element={<LanguageRouteWrapper data-id="element-54">
            <PrivacyPolicyPage data-id="element-55" />
          </LanguageRouteWrapper>} data-id="element-53" />

      {/* Terms of Service - syarat-ketentuan (ID) / terms-of-service (EN) */}
      <Route path="/:lang/syarat-ketentuan" element={<LanguageRouteWrapper data-id="element-57">
            <TermsOfServicePage data-id="element-58" />
          </LanguageRouteWrapper>} data-id="element-56" />
      <Route path="/:lang/terms-of-service" element={<LanguageRouteWrapper data-id="element-60">
            <TermsOfServicePage data-id="element-61" />
          </LanguageRouteWrapper>} data-id="element-59" />

      {/* Legacy routes without language prefix - redirect to /id with Indonesian slug */}
      <Route path="/tentang-kami" element={<Navigate to="/id/tentang-kami" replace data-id="element-63" />} data-id="element-62" />
      <Route path="/about-us" element={<Navigate to="/en/about-us" replace data-id="element-65" />} data-id="element-64" />
      <Route path="/manajemen" element={<Navigate to="/id/manajemen" replace data-id="element-67" />} data-id="element-66" />
      <Route path="/management" element={<Navigate to="/en/management" replace data-id="element-69" />} data-id="element-68" />
      <Route path="/proyek" element={<Navigate to="/id/proyek" replace data-id="element-71" />} data-id="element-70" />
      <Route path="/projects" element={<Navigate to="/en/projects" replace data-id="element-73" />} data-id="element-72" />
      <Route path="/produk-layanan" element={<Navigate to="/id/produk-layanan" replace data-id="element-75" />} data-id="element-74" />
      <Route path="/products-services" element={<Navigate to="/en/products-services" replace data-id="element-77" />} data-id="element-76" />
      <Route path="/keunggulan" element={<Navigate to="/id/keunggulan" replace data-id="element-79" />} data-id="element-78" />
      <Route path="/advantages" element={<Navigate to="/en/advantages" replace data-id="element-81" />} data-id="element-80" />
      <Route path="/konsultasi" element={<Navigate to="/id/konsultasi" replace data-id="element-83" />} data-id="element-82" />
      <Route path="/consultation" element={<Navigate to="/en/consultation" replace data-id="element-85" />} data-id="element-84" />
      <Route path="/kontak" element={<Navigate to="/id/kontak" replace data-id="element-87" />} data-id="element-86" />
      <Route path="/contact" element={<Navigate to="/en/contact" replace data-id="element-89" />} data-id="element-88" />
      <Route path="/kebijakan-privasi" element={<Navigate to="/id/kebijakan-privasi" replace data-id="element-91" />} data-id="element-90" />
      <Route path="/privacy-policy" element={<Navigate to="/en/privacy-policy" replace data-id="element-93" />} data-id="element-92" />
      <Route path="/syarat-ketentuan" element={<Navigate to="/id/syarat-ketentuan" replace data-id="element-95" />} data-id="element-94" />
      <Route path="/terms-of-service" element={<Navigate to="/en/terms-of-service" replace data-id="element-97" />} data-id="element-96" />
    </Routes>;
}
function App() {
  return <LanguageProvider data-id="element-98">
      <Router data-id="element-99">
        <ScrollToTop data-id="element-100" />
        <div className="flex flex-col min-h-screen" data-id="element-101">
          <Header data-id="element-102" />
          <main className="flex-grow" data-id="element-103">
            <AppRoutes data-id="element-104" />
          </main>
          <Footer data-id="element-105" />
        </div>
      </Router>
    </LanguageProvider>;
}
export { App };